import { observable, action, computed, reaction } from 'mobx';
import { isFunction } from 'lodash';
import { api } from '../services';
import { filterStore } from 'air-filter-mobx-store';
import { commonStore, translationsStore } from './index';
import moment from 'moment';

class SubscriptionsStore {
  @observable subscriptions = [];
  @observable freeSubscriptions = [];
  @observable loading;
  @observable loadedAt;

  constructor(filterStore) {
    this.filterStore = filterStore;
    this.commonStore = commonStore;
    this.translationsStore = translationsStore;

    reaction(
      () => this.filterStore.globalFilterFunction,
      fn => {
        const subscriptions = this.subscriptions.map(subscription => {
          if (fn(subscription)) {
            return { ...subscription, hidden: false };
          } else {
            return { ...subscription, hidden: true };
          }
        });
        this.setsubscriptions(subscriptions);
      }
    );
  }

  @action
  async load() {
    this.loading = true;

    if (!this.commonStore.dealer) {
      await api('dealer profile').then(dealer => {
        commonStore.setDealer(dealer);
      });
    }

    return api('getSubscriptions', this.commonStore.dealer.masterApiUserUuid)
      .then(results => {
        const directManageableContractsPaid = results.filter(contract => contract.status !== 'unpaid'),
          contractsHavingBaseProducts = directManageableContractsPaid.filter(contract => contract.product.category === 'base');
        let subscriptions = [];
        contractsHavingBaseProducts.forEach((contract, index) => {
          // INFO contract.vehicle, contract.installation from API are arrays
          const contractActiveVehicle = contract.vehicle.find(vehicle => vehicle.till === undefined || (vehicle.hasOwnProperty('till') && moment().isBefore(vehicle.till)));
          const contractActiveInstallation = contract.installation.find(
            installation => installation.till === undefined || (installation.hasOwnProperty('till') && moment().isBefore(installation.till))
          );
          const foundVehicle = contract.vehicle.find(vehicle => vehicle.hasOwnProperty('till'));
          subscriptions.push({
            products: [{...contract.product, portability_reference_contract_uuid: contract.uuid}],//the first one in the products array must be the main product
            subscriptionType: contract.subscriptionType,
            start_date: contract.start_date,
            end_date: contract.end_date,
            vehicle: contractActiveVehicle ? contractActiveVehicle.node :
              foundVehicle ? foundVehicle.node : null,
            installation: contractActiveInstallation,
            available: !contractActiveVehicle
          });
        });
        const contractsHavingNonBaseProducts = directManageableContractsPaid.filter(contract => contract.product.category !== 'base');
        contractsHavingNonBaseProducts.forEach((contract, index) => {
          const subscriptionIndex = subscriptions.findIndex(subscription => subscription.vehicle && subscription.vehicle.license_plate === contract.vehicle[0].node.license_plate);
          if (subscriptionIndex !== -1) {
            subscriptions[subscriptionIndex].products.push({...contract.product, portability_reference_contract_uuid: contract.uuid});
          }
        });
        subscriptions = subscriptions.sort((a, b) => (a.start_date > b.start_date ? 1 : b.start_date > a.start_date ? -1 : 0));
        this.setsubscriptions(subscriptions);
        this.setLoadedAt();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  @action setLoadedAt() {
    this.loadedAt = new Date().getTime();
  }

  @action
  setsubscriptions(subscriptions) {
    this.subscriptions = subscriptions;
    this.freeSubscriptions = subscriptions.filter(subscription => subscription.available && new Date(subscription.end_date) >= new Date());
  }

  @computed get filtered() {
    if (this.filterStore.globalFilterFunction) {
      return this.subscriptions.filter(this.filterStore.globalFilterFunction);
    } else {
      return this.subscriptions;
    }
  }

  filterLength = (filterType, attribute) => {
    if (isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this[attribute].filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    return this[attribute].length;
  };

  groupsLength = (filterType, attribute) => {
    if (attribute === 'all' && isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this.subscriptions.filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    if (attribute === 'free' && isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this.subscriptions.filter(subscription => subscription.available && new Date(subscription.end_date) >= new Date() ).filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
    if (attribute === 'combined' && isFunction(this.filterStore.predictionFns[`${filterType}.${attribute}`])) {
      return this.subscriptions.filter(subscription => !subscription.available ).filter(this.filterStore.predictionFns[`${filterType}.${attribute}`]).length;
    }
  };

  textSearchFns = searchText => subscription => {
    if (!searchText || searchText.length < 1) {
      return true;
    }
    if (subscription.vehicle && subscription.vehicle.license_plate && subscription.vehicle.license_plate.toLowerCase().includes(searchText.toLowerCase())) {
      return true;
    }
    if (subscription.products.map(el => el.name).some(el => el.toLowerCase().includes(searchText.toLowerCase()))) {
      return true;
    }
    if (
      subscription.subscriptionType &&
      subscription.subscriptionType.name &&
      this.translationsStore
        .translate(`connectionRequests.subscriptionType.${subscription.subscriptionType.name}`)
        .toLowerCase()
        .includes(searchText.toLowerCase())
    ) {
      return true;
    }
    if (
      subscription.end_date &&
      moment(subscription.end_date)
        .format('DD/MM/YYYY')
        .includes(searchText)
    ) {
      return true;
    }
    if (
      subscription.end_date &&
      translationsStore
        .translate(`subscriptionManager.statuses.${moment(subscription.end_date).isBefore(moment()) ? 'expired' : 'active'}`)
        .toLowerCase()
        .includes(searchText.toLowerCase())
    ) {
      return true;
    }
    if (subscription.installation && subscription.installation.node && subscription.installation.node.asset && subscription.installation.node.asset.ain.includes(searchText)) {
      return true;
    }
    return false;
  };

  @computed get endDateYears() {
    return this.filtered.map(subscription => (moment(subscription.end_date).format('YYYY') ? moment(subscription.end_date).format('YYYY') : -1));
  }

  @computed get registrationYearMin() {
    return Math.min(...this.endDateYears);
  }

  @computed get registrationYearMax() {
    return Math.max(...this.endDateYears);
  }

  @computed
  get subscriptionTypes() {
    return this.subscriptions
      .map(subscription => subscription.subscriptionType.name)
      .filter((subscriptionType, index, subscriptionTypes) => subscriptionTypes.indexOf(subscriptionType) === index)
      .sort((a, b) => this.translationsStore.translate(`subscriptionManager.types.${a}`).localeCompare(this.translationsStore.translate(`subscriptionManager.types.${b}`)));
  }

  @computed
  get subscriptionStatuses() {
    return ['active', 'expired'];
  }

  @computed
  get mainProducts() {
    return [].concat
      .apply([], this.subscriptions.map(subscription => subscription.products.map(product => product.name)))
      .filter((productName, index, self) => self.indexOf(productName) === index)
      .sort();
  }
}

export default new SubscriptionsStore(filterStore, commonStore, translationsStore);



// WEBPACK FOOTER //
// ./src/stores/subscriptionsStore.js